import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import LoupeIcon from '@mui/icons-material/Loupe';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { Box, Chip, IconButton, Rating, Tooltip, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

import CustomBackdrop from '../../components/CustomBackdrop/CustomBackdrop';
import {
  BuyAndBuildListDocument,
  useBuyAndBuildListLazyQuery,
  useRemoveShortlistedBuyAndBuildTargetsMutation,
  useShortlistBuyAndBuildTargetMutation,
} from '../../gql/generated/graphql';
import { handleVisitPage } from '../../helpers/helpers';
import usePagination from '../../hooks/usePagination';
import CustomPaper from '../DFMPage/components/CustomPaper/CustomPaper';

import { MUI_RATING_SCALE } from './BuyBuildPage';
import { useTargetRating } from './hooks/useTargetRating';

import styles from './styles';

type Props = {
  companyId: string | null;
  markCompanyAsNotSoftwareLoading: boolean;
  handleValidate: (companyId: number) => void;
  handleNavigateToAssesment: (companyId: number) => void;
  handleMarkAsNotSoftware: (id: number) => void;
  rateAndShortlistEnabled?: boolean;
  ratingFilter?: 'all' | 'manual' | 'automated';
};

export const BBLonglist = ({
  companyId,
  markCompanyAsNotSoftwareLoading,
  handleValidate,
  handleMarkAsNotSoftware,
  handleNavigateToAssesment,
  rateAndShortlistEnabled = false,
  ratingFilter = 'all',
}: Props) => {
  const { t } = useTranslation();
  const [getBuyAndBuild, { data: buyAndBuild, loading: buyAndBuildListLoading, fetchMore, refetch }] =
    useBuyAndBuildListLazyQuery();

  const { handleRateTarget, rateBuyAndBuildLoading } = useTargetRating(companyId);

  const [addToShortlist, { loading: addToShortlistLoading }] = useShortlistBuyAndBuildTargetMutation();
  const [removeFromShortlist, { loading: removeFromShortlistLoading }] =
    useRemoveShortlistedBuyAndBuildTargetsMutation();

  useEffect(() => {
    if (!companyId) return;

    getBuyAndBuild({
      variables: { companyId: parseInt(companyId), first: 10 },
      fetchPolicy: 'network-only',
    });
  }, [companyId]);

  const buyAndBuildPageInfo = buyAndBuild?.companyAssesment?.edges?.[0]?.node?.buyAndBuildTargets?.pageInfo;
  const buyAndBuildData = buyAndBuild?.companyAssesment?.edges?.[0]?.node?.buyAndBuildTargets?.edges?.map(
    node => node?.node
  );
  const buyAndBuildDataFormatted = buyAndBuildData
    ?.map(node => ({
      ...node.company,
      buyAndBuildTargetRelevance: node.buyAndBuildRelevance,
      isShortlisted: node.isShortlistedCompany1,
      manuallyLabeledScore: node.manuallyLabeledScore,
    }))
    .filter(row => {
      if (ratingFilter === 'manual') {
        return row.manuallyLabeledScore !== null && row.manuallyLabeledScore !== undefined;
      } else if (ratingFilter === 'automated') {
        return row.manuallyLabeledScore === null || row.manuallyLabeledScore === undefined;
      }
      return true; // 'all'
    });

  const { paginationModel, isLoading, handlePaginationChange } = usePagination({
    fetchMore,
    pageInfo: buyAndBuildPageInfo,
  });

  const longlistColumns = [
    {
      field: 'uniqueDomain',
      headerName: t('buyAndBuild.urlColumnField'),
      width: 200,
    },
    {
      field: 'validated',
      headerName: '',
      width: 100,
      renderCell: params => (
        <Box display="flex" alignItems="center" height="100%">
          {params.value && <Chip label="Validated" color="success" size="small" />}
        </Box>
      ),
    },
    {
      field: 'country',
      headerName: t('buyAndBuild.countryColumnField'),
      width: 100,
    },
    {
      field: 'fte',
      headerName: t('buyAndBuild.fteColumnField'),
      width: 100,
    },
    {
      field: 'productCategorization',
      headerName: t('buyAndBuild.productColumnField'),
      headerAlign: 'center' as const,
      width: 250,
      valueGetter: params => {
        const categorizations = params?.edges;
        if (!categorizations || categorizations.length === 0) return [];

        // Get the first categorization with highest certainty
        const sorted = [...categorizations].sort(
          (a, b) => (b?.node?.softwareCategoryCertainty || 0) - (a?.node?.softwareCategoryCertainty || 0)
        );

        const category = sorted[0]?.node?.softwareCategory;
        if (!category) return [];

        // Return array of subcategories, filtering out null/empty values
        return [category.subCategory1, category.subCategory2, category.subCategory3].filter(Boolean);
      },
      renderCell: params => (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            gap: 1,
          }}
        >
          {params.formattedValue?.map((subcategory, index) => (
            <Chip key={`${subcategory}-${index}`} label={subcategory} size="small" />
          ))}
        </Box>
      ),
    },
    {
      field: 'latestCrawlResult',
      headerName: t('buyAndBuild.keywordsColumnField'),
      headerAlign: 'center' as const,
      width: 200,
      valueGetter: params => {
        const keywords = params?.xoviKeywords?.edges?.slice(0, 2).map(node => node?.node);
        return keywords;
      },
      renderCell: params => (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            gap: 1,
          }}
        >
          {params.formattedValue?.map((node, index) => (
            <Chip key={node?.keyword + node?.cpc + index} label={node?.keyword} size="small" />
          ))}
        </Box>
      ),
    },
    {
      field: 'summaryDescription',
      headerName: t('buyAndBuild.summaryColumnField'),
      width: 400,
      renderCell: params => (
        <Box display="flex" sx={{ alignItems: 'center', height: '100%' }}>
          <Typography
            variant="body2"
            sx={{
              textWrap: 'wrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              ellipsizeMode: 'tail',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 3,
            }}
          >
            {params?.formattedValue}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'pipedriveLink',
      headerName: '',
      width: 60,
      renderCell: params => (
        <Box display="flex" alignItems="center" height="100%">
          {params?.row?.pipedriveId && (
            <IconButton
              size="small"
              onClick={() =>
                window.open(`https://bid.pipedrive.com/organization/${params?.row?.pipedriveId}`, '_blank')
              }
            >
              <Tooltip title="Open in Pipedrive">
                <LoupeIcon fontSize="small" color="primary" />
              </Tooltip>
            </IconButton>
          )}
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 300,
      renderCell: params => {
        const isShortlisted = params?.row?.isShortlistedCompany1;
        const ratingValue = params?.row?.manuallyLabeledScore
          ? params?.row?.manuallyLabeledScore
          : params?.row?.buyAndBuildTargetRelevance;
        return (
          <Box display="flex" height="100%">
            <IconButton size="small" onClick={() => handleVisitPage(params?.row?.uniqueDomain)}>
              <Tooltip title={t('buyAndBuild.visitButtonTooltip')}>
                <LanguageIcon fontSize="small" />
              </Tooltip>
            </IconButton>
            <IconButton size="small" onClick={() => handleValidate(params.row?.dbId)}>
              <Tooltip title={t('buyAndBuild.validateButtonTooltip')}>
                <AssignmentTurnedInOutlinedIcon fontSize="small" />
              </Tooltip>
            </IconButton>
            <IconButton size="small" onClick={() => handleNavigateToAssesment(params.row?.dbId)}>
              <Tooltip title={t('buyAndBuild.evaluateButtonTooltip')}>
                <FactCheckOutlinedIcon fontSize="small" />
              </Tooltip>
            </IconButton>
            <IconButton size="small" onClick={() => handleMarkAsNotSoftware(params.row?.dbId)}>
              <Tooltip title={t('buyAndBuild.markAsNotSoftwareButton')}>
                <AssignmentTurnedInOutlinedIcon fontSize="small" />
              </Tooltip>
            </IconButton>
            <IconButton size="small" onClick={() => handleAction(params.row)}>
              <Tooltip
                title={
                  isShortlisted
                    ? t('buyAndBuild.removeFromShortListColumnTooltip')
                    : t('buyAndBuild.addToShortlistColumnTooltip')
                }
              >
                {isShortlisted ? <StarIcon fontSize="small" /> : <StarOutlineIcon fontSize="small" />}
              </Tooltip>
            </IconButton>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Tooltip title={t('buyAndBuild.rateTarget')}>
                <Rating
                  name="rating"
                  sx={styles.rating}
                  value={ratingValue * MUI_RATING_SCALE}
                  precision={0.1}
                  data-testid={`bb-rating-${params.row.dbId}`}
                  onChange={async (_, newValue) => {
                    await handleRateTarget(params.row.dbId, newValue);
                    if (
                      rateAndShortlistEnabled &&
                      !params?.row?.isShortlistedCompany1 &&
                      (newValue || 0) >= MUI_RATING_SCALE
                    ) {
                      // Optimistically shortlist after rating when toggle is enabled
                      const variables = {
                        softwareCompanyId: buyAndBuild?.companyAssesment?.edges?.[0]?.node?.dbId,
                        targetCompanyId: params.row?.dbId,
                      };
                      await addToShortlist({
                        variables,
                        optimisticResponse: {
                          shortlistBuyAndBuildTarget: { __typename: 'ShortlistBuyAndBuildTarget', ok: true },
                        },
                        update: cache => {
                          const baseVars: any = { companyId: parseInt(companyId as string), first: 10 };
                          try {
                            cache.updateQuery({ query: BuyAndBuildListDocument, variables: baseVars }, (data: any) => {
                              if (!data) return data;
                              const next = { ...data };
                              const edges = next?.companyAssesment?.edges;
                              if (!edges || !edges[0]?.node?.buyAndBuildTargets?.edges) return data;
                              edges[0].node.buyAndBuildTargets.edges = edges[0].node.buyAndBuildTargets.edges.map(
                                (edge: any) => {
                                  if (edge?.node?.company?.dbId === params.row?.dbId) {
                                    return {
                                      ...edge,
                                      node: { ...edge.node, isShortlistedCompany1: true },
                                    };
                                  }
                                  return edge;
                                }
                              );
                              return next;
                            });
                          } catch (_) {}
                          // Also try shortlist view cache if present
                          try {
                            cache.updateQuery(
                              { query: BuyAndBuildListDocument, variables: { ...baseVars, isShortlisted: true } },
                              (data: any) => data
                            );
                          } catch (_) {}
                        },
                      });
                    }
                  }}
                />
              </Tooltip>
              <Tooltip
                title={
                  params?.row?.manuallyLabeledScore !== null && params?.row?.manuallyLabeledScore !== undefined
                    ? 'Manual Rating'
                    : 'Automated Rating'
                }
              >
                {params?.row?.manuallyLabeledScore !== null && params?.row?.manuallyLabeledScore !== undefined ? (
                  <PersonIcon fontSize="small" color="primary" />
                ) : (
                  <SmartToyIcon fontSize="small" color="action" />
                )}
              </Tooltip>
            </Box>
          </Box>
        );
      },
    },
  ];

  const handleAction = async row => {
    const variables = {
      softwareCompanyId: buyAndBuild?.companyAssesment?.edges?.[0]?.node?.dbId,
      targetCompanyId: row?.dbId,
    };

    if (row?.isShortlistedCompany1) {
      await removeFromShortlist({
        variables,
        optimisticResponse: {
          removeShortlistedBuyAndBuildTargets: { __typename: 'RemoveShortlistedBuyAndBuildTarget', ok: true },
        },
        update: cache => {
          const baseVars: any = { companyId: parseInt(companyId as string), first: 10 };
          try {
            cache.updateQuery({ query: BuyAndBuildListDocument, variables: baseVars }, (data: any) => {
              if (!data) return data;
              const next = { ...data };
              const edges = next?.companyAssesment?.edges;
              if (!edges || !edges[0]?.node?.buyAndBuildTargets?.edges) return data;
              edges[0].node.buyAndBuildTargets.edges = edges[0].node.buyAndBuildTargets.edges.map((edge: any) => {
                if (edge?.node?.company?.dbId === row?.dbId) {
                  return { ...edge, node: { ...edge.node, isShortlistedCompany1: false } };
                }
                return edge;
              });
              return next;
            });
          } catch (_) {}
        },
      });
    } else if (!row?.isShortlistedCompany1) {
      await addToShortlist({
        variables,
        optimisticResponse: {
          shortlistBuyAndBuildTarget: { __typename: 'ShortlistBuyAndBuildTarget', ok: true },
        },
        update: cache => {
          const baseVars: any = { companyId: parseInt(companyId as string), first: 10 };
          try {
            cache.updateQuery({ query: BuyAndBuildListDocument, variables: baseVars }, (data: any) => {
              if (!data) return data;
              const next = { ...data };
              const edges = next?.companyAssesment?.edges;
              if (!edges || !edges[0]?.node?.buyAndBuildTargets?.edges) return data;
              edges[0].node.buyAndBuildTargets.edges = edges[0].node.buyAndBuildTargets.edges.map((edge: any) => {
                if (edge?.node?.company?.dbId === row?.dbId) {
                  return { ...edge, node: { ...edge.node, isShortlistedCompany1: true } };
                }
                return edge;
              });
              return next;
            });
          } catch (_) {}
        },
      });
    }
  };

  const showBackdrop =
    addToShortlistLoading || removeFromShortlistLoading || markCompanyAsNotSoftwareLoading || rateBuyAndBuildLoading;

  return (
    <Box sx={styles.box}>
      <CustomPaper>
        <DataGrid
          paginationMode="server"
          loading={buyAndBuildListLoading || isLoading}
          disableRowSelectionOnClick
          autoHeight
          rows={buyAndBuildDataFormatted || []}
          getRowId={row => row?.dbId}
          columns={longlistColumns}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationChange}
          rowHeight={75}
          initialState={{
            pagination: {
              rowCount: -1,
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          // slots={{ toolbar: GridToolbar }}
          pageSizeOptions={[10]}
        />
      </CustomPaper>
      <CustomBackdrop open={showBackdrop} />
    </Box>
  );
};
